/**
 * 🗺️ PublicRoadmap — Wave 4 · 24-month clickable timeline
 * ════════════════════════════════════════════════════════════════════════
 *
 * Public page at /roadmap. Renders the 8 canonical checkpoint cards
 * from PROGRAM_TIMELINE with clickable phase drawers and anonymized
 * per-phase aggregate stats pulled from /api/v1/public/roadmap.
 *
 * The `<RoadmapPreview />` named export is a compact 8-dot rail used
 * on the homepage — deep-links to /roadmap#phase-id on click.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRight, ArrowLeft, Users, Award, Briefcase, Handshake, Heart, Sparkles, X as XIcon } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentMeta from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

const STAT_META = [
  { key: "residents_reached",    label: "Residents Reached",    icon: <Users className="h-4 w-4" /> },
  { key: "graduates_by_phase",   label: "Graduates by Phase",   icon: <Award className="h-4 w-4" /> },
  { key: "labor_hours",          label: "Labor Hours",          icon: <Briefcase className="h-4 w-4" />, fmt: (n) => (n || 0).toLocaleString() },
  { key: "partner_count",        label: "Partner Orgs",         icon: <Handshake className="h-4 w-4" /> },
  { key: "donor_dollars",        label: "Donor $ Raised",       icon: <Heart className="h-4 w-4" />, fmt: (n) => "$" + (n || 0).toLocaleString() },
  { key: "checkpoints_reviewed", label: "Checkpoints Reviewed", icon: <Sparkles className="h-4 w-4" /> },
];


export default function PublicRoadmap() {
  useDocumentMeta({
    title:       "24-Month Roadmap | The Exchange Alliance",
    description: "The public path from stabilization to graduation — 8 checkpoints across 24 months, anchored in real program data.",
    canonical:   "https://theexchangealliance.com/roadmap",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [data, setData] = useState(null);
  const [openPhaseId, setOpenPhaseId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/v1/public/roadmap`, { withCredentials: false })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  // Deep-link support: /roadmap#d90 opens that phase drawer on load.
  useEffect(() => {
    if (!data) return;
    const hash = window.location.hash.replace("#", "");
    if (hash && data.phases.find((p) => p.id === hash)) setOpenPhaseId(hash);
  }, [data]);

  const openPhase = useMemo(() => data?.phases.find((p) => p.id === openPhaseId) || null, [data, openPhaseId]);

  return (
    <main data-testid="public-roadmap-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      <Header />
      <section className="pt-28 pb-10 md:pt-36 md:pb-14 border-b border-[#0A0612]/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A227]/50 bg-[#FFFBEC] font-mono text-[10px] tracking-[0.22em] uppercase mb-6">
            <Sparkles className="h-3 w-3 text-[#C9A227]" /> The 24-Month Path
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6 max-w-4xl">
            From the encampment to the front door of homeownership — <span className="text-[#1B4332]">in 730 days.</span>
          </h1>
          <p className="text-lg md:text-xl text-[#0A0612]/70 max-w-3xl leading-relaxed">
            Every resident moves through the same 8 checkpoints. Below is a live, anonymized view of where the current cohort stands — click any card to drill into the phase mission and the aggregate impact.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20" data-testid="roadmap-phase-grid">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
          {!data && (
            <div className="text-center font-mono text-xs uppercase tracking-[0.22em] text-[#6B6F76]">Loading roadmap…</div>
          )}
          {data && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {data.phases.map((p, idx) => (
                <PhaseCard key={p.id} phase={p} index={idx} onOpen={() => setOpenPhaseId(p.id)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {data && (
        <section className="py-14 md:py-20 bg-white border-y border-[#0A0612]/5" data-testid="roadmap-totals">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#C9A227] mb-2">Aggregate Since Launch</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mb-10">
              The whole picture — anonymized, real, right now.
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
              <TotalCard label="Residents Reached" value={data.totals.residents} />
              <TotalCard label="Graduates" value={data.totals.graduates} />
              <TotalCard label="Labor Hours" value={data.totals.labor_hours.toLocaleString()} />
              <TotalCard label="Partner Orgs" value={data.totals.partners} />
              <TotalCard label="Donors" value={data.totals.donors} />
              <TotalCard label="Donor $ Raised" value={"$" + data.totals.donor_dollars.toLocaleString()} />
            </div>
          </div>
        </section>
      )}

      {openPhase && (
        <PhaseDrawer phase={openPhase} onClose={() => setOpenPhaseId(null)} />
      )}

      <Footer />
    </main>
  );
}


/** Named export: the compact rail used on the homepage. */
export function RoadmapPreview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/v1/public/roadmap`, { withCredentials: false })
      .then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <section
      id="journey"
      className="bg-[#0A0612] text-white py-14 md:py-20 border-y border-[#F4D88A]/20"
      data-testid="roadmap-preview"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8 md:mb-12">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4D88A]/80 mb-3">
              The 24-Month Roadmap
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl">
              8 checkpoints. 730 days. Zero shortcuts.
            </h2>
          </div>
          <a
            href="/roadmap"
            data-testid="roadmap-preview-cta"
            className="inline-flex items-center gap-2 border border-[#F4D88A]/50 text-[#F4D88A] px-5 py-3 font-mono text-[10px] tracking-[0.22em] uppercase hover:bg-[#F4D88A] hover:text-[#0A0612] transition-colors"
          >
            Explore Full Roadmap <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        {data && (
          <div className="relative">
            {/* Horizontal timeline rail with markers */}
            <div className="hidden md:block absolute left-0 right-0 top-6 h-px bg-white/15" />
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 relative">
              {data.phases.map((p, idx) => (
                <a
                  key={p.id}
                  href={`/roadmap#${p.id}`}
                  data-testid={`roadmap-preview-dot-${p.id}`}
                  className="group flex flex-col items-center text-center"
                >
                  <div className="relative">
                    <div className="h-3 w-3 rounded-full bg-[#F4D88A] ring-4 ring-[#0A0612] group-hover:ring-[#1B4332] transition-colors" />
                  </div>
                  <div className="mt-4 font-mono text-[9px] tracking-[0.22em] uppercase text-[#F4D88A]/70">
                    {p.month_offset_display}
                  </div>
                  <div className="mt-1 text-sm font-bold group-hover:text-[#F4D88A] transition-colors leading-snug">
                    {p.label.replace(/^\d+-Day |^\d+-Month /g, "").replace(" Checkpoint", "").replace(" Review", "")}
                  </div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-white/50 tabular-nums">
                    {p.stats.residents_reached} reached
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────
function PhaseCard({ phase, index, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`roadmap-phase-card-${phase.id}`}
      className="text-left rounded-2xl border-2 border-[#0A0612]/10 bg-white hover:border-[#C9A227] hover:shadow-xl transition-all p-6 flex flex-col group"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9A227]">
          Phase {index + 1}
        </div>
        <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#0A0612]/50">
          {phase.month_offset_display}
        </div>
      </div>
      <div className="font-display text-xl font-bold tracking-tight text-[#0A0612] leading-tight mb-3">
        {phase.label}
      </div>
      <div className="text-sm text-[#0A0612]/70 leading-relaxed flex-1 mb-5">
        {phase.description}
      </div>
      <div className="flex items-baseline justify-between pt-4 border-t border-[#0A0612]/10">
        <div>
          <div className="font-display text-3xl font-bold tabular-nums text-[#1B4332]" data-testid={`roadmap-phase-reached-${phase.id}`}>
            {phase.stats.residents_reached}
          </div>
          <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-[#0A0612]/50">
            residents reached
          </div>
        </div>
        <div className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#0A0612] group-hover:text-[#1B4332] transition-colors">
          Open <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </button>
  );
}


function PhaseDrawer({ phase, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-0 md:p-6" onClick={onClose} data-testid={`roadmap-phase-drawer-${phase.id}`}>
      <div className="w-full max-w-3xl bg-white max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 md:p-10 border-b border-[#0A0612]/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9A227] mb-2">
                {phase.month_offset_display}
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0A0612]">
                {phase.label}
              </h3>
            </div>
            <button type="button" onClick={onClose}
              data-testid={`roadmap-phase-drawer-close-${phase.id}`}
              className="rounded-full h-9 w-9 flex items-center justify-center hover:bg-[#0A0612]/5">
              <XIcon className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-4 text-base md:text-lg text-[#0A0612]/70 leading-relaxed">
            {phase.description}
          </p>
        </div>

        <div className="p-6 md:p-10 border-b border-[#0A0612]/10">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0A0612]/60 mb-3">Focus Areas</div>
          <div className="flex flex-wrap gap-2">
            {(phase.focus_areas || []).map((f) => (
              <span key={f} className="px-3 py-1.5 rounded-full bg-[#1B4332] text-[#F4D88A] font-mono text-[10px] tracking-[0.18em] uppercase">
                {f}
              </span>
            ))}
          </div>
        </div>

        <div className="p-6 md:p-10">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0A0612]/60 mb-4">
            Anonymized Aggregate — through this phase
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {STAT_META.map((meta) => {
              const v = phase.stats[meta.key] ?? 0;
              const formatted = meta.fmt ? meta.fmt(v) : v.toLocaleString();
              return (
                <div key={meta.key} className="rounded-2xl border border-[#0A0612]/10 p-4 bg-[#FAFAF7]"
                  data-testid={`roadmap-drawer-stat-${meta.key}-${phase.id}`}>
                  <div className="text-[#C9A227] mb-2">{meta.icon}</div>
                  <div className="font-display text-2xl font-bold tabular-nums text-[#0A0612]">{formatted}</div>
                  <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-[#0A0612]/60 mt-1">
                    {meta.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


function TotalCard({ label, value }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl font-bold tabular-nums text-[#0A0612]">{value}</div>
      <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#6B6F76] mt-2">{label}</div>
    </div>
  );
}
